package org.example.project.planner

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import org.example.project.client.api.ActivityApiClient
import org.example.project.client.error.ConflictException
import org.example.project.client.error.NetworkException
import org.example.project.client.error.UnauthorizedException
import org.example.project.client.model.ActivityFilters
import org.example.project.client.model.ActivityResponse
import org.example.project.client.model.BlockType
import org.example.project.client.model.CreateEventBlockRequest
import org.example.project.client.model.CreateEventRequest
import org.example.project.client.model.EventResponse
import org.example.project.client.model.UpdateEventBlockRequest
import org.example.project.client.model.UpdateEventRequest
import org.example.project.client.session.SessionRepository
import kotlin.random.Random

fun generateClientUuid(): String {
    val bytes = Random.nextBytes(16)
    bytes[6] = ((bytes[6].toInt() and 0x0f) or 0x40).toByte()
    bytes[8] = ((bytes[8].toInt() and 0x3f) or 0x80).toByte()
    return buildString(36) {
        for (i in 0 until 16) {
            if (i == 4 || i == 6 || i == 8 || i == 10) append('-')
            val b = bytes[i].toInt() and 0xff
            append(b.toString(16).padStart(2, '0'))
        }
    }
}

fun isValidIsoDate(dateStr: String): Boolean {
    val trimmed = dateStr.trim()
    if (!trimmed.matches(Regex("""^\d{4}-\d{2}-\d{2}$"""))) return false
    val parts = trimmed.split("-")
    if (parts.size != 3) return false
    val year = parts[0].toIntOrNull() ?: return false
    val month = parts[1].toIntOrNull() ?: return false
    val day = parts[2].toIntOrNull() ?: return false

    if (year < 1 || month !in 1..12 || day < 1) return false

    val maxDays = when (month) {
        1, 3, 5, 7, 8, 10, 12 -> 31
        4, 6, 9, 11 -> 30
        2 -> {
            val isLeap = (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)
            if (isLeap) 29 else 28
        }
        else -> return false
    }

    return day <= maxDays
}

class PlannerViewModel(
    private val plannerRepository: PlannerRepository,
    private val sessionRepository: SessionRepository? = null,
    private val activityApiClient: ActivityApiClient? = null,
    private val saveDebounceMillis: Long = 300L
) : ViewModel() {

    private val _uiState = MutableStateFlow(PlannerUiState())
    val uiState: StateFlow<PlannerUiState> = _uiState.asStateFlow()

    private var isEventsLoading = false
    private var isFavoritesLoading = false

    private var debounceSaveJob: Job? = null
    private val saveMutex = Mutex()

    init {
        if (sessionRepository != null) {
            viewModelScope.launch {
                var wasAuthenticated = false
                sessionRepository.sessionState.collect { session ->
                    if (session.isAuthenticated) {
                        wasAuthenticated = true
                        loadEvents()
                        loadFavorites()
                    } else {
                        if (wasAuthenticated) {
                            wasAuthenticated = false
                            clearData()
                        }
                    }
                }
            }
        }
    }

    fun clearData() {
        debounceSaveJob?.cancel()
        debounceSaveJob = null
        _uiState.update {
            it.copy(
                events = emptyList(),
                favorites = emptyList(),
                favoriteIds = emptySet(),
                catalogActivities = emptyList(),
                currentEvent = null,
                generalError = null,
                saveStatus = SaveStatus.Idle,
                viewMode = PlannerViewMode.CreateEvent,
                isTimelineEditMode = false,
                selectedBlockId = null,
                eventDraft = createDefaultDraft()
            )
        }
    }

    fun loadEvents() {
        if (sessionRepository != null && !sessionRepository.sessionState.value.isAuthenticated) {
            return
        }
        if (isEventsLoading) return
        isEventsLoading = true

        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, generalError = null) }
            try {
                val events = plannerRepository.listEvents()
                _uiState.update { it.copy(isLoading = false, events = events) }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        generalError = e.message ?: "Nie udało się pobrać listy wydarzeń"
                    )
                }
            } finally {
                isEventsLoading = false
            }
        }
    }

    fun loadFavorites() {
        if (sessionRepository != null && !sessionRepository.sessionState.value.isAuthenticated) {
            return
        }
        if (isFavoritesLoading) return
        isFavoritesLoading = true

        viewModelScope.launch {
            try {
                val favResponse = plannerRepository.getFavorites()
                _uiState.update {
                    it.copy(
                        favorites = favResponse.items,
                        favoriteIds = favResponse.activityIds.toSet()
                    )
                }
            } catch (e: CancellationException) {
                throw e
            } catch (_: Exception) {
                // Silently keep current favorites on failure
            } finally {
                isFavoritesLoading = false
            }
        }
    }

    private fun flushCurrentEventSave() {
        val hadPendingDebounce = (debounceSaveJob?.isActive == true)
        debounceSaveJob?.cancel()
        debounceSaveJob = null

        val state = _uiState.value
        val draft = state.eventDraft
        val mode = state.viewMode

        val isCreate = (mode is PlannerViewMode.CreateEvent)
        val shouldFlush = if (isCreate) {
            !isUntouchedDraft(draft) && validateDraft(draft) == null
        } else {
            val existing = state.events.firstOrNull { it.id == draft.id }
            validateDraft(draft) == null && (existing == null || draft.copy(version = existing.version) != existing.toDraft())
        }

        if (shouldFlush || hadPendingDebounce) {
            viewModelScope.launch {
                saveDraftInternal(draft, isCreate, isExplicit = false)
            }
        }
    }

    fun openEventList() {
        flushCurrentEventSave()
        _uiState.update {
            it.copy(
                viewMode = PlannerViewMode.EventList,
                isTimelineEditMode = false,
                selectedBlockId = null,
                saveStatus = SaveStatus.Idle,
                validationError = null
            )
        }
        loadEvents()
    }

    fun backFromEventList() {
        _uiState.update { state ->
            val mode = if (state.currentEvent != null) {
                PlannerViewMode.EventDetail(state.currentEvent.id)
            } else {
                PlannerViewMode.CreateEvent
            }
            state.copy(viewMode = mode)
        }
    }

    fun startCreateEvent() {
        flushCurrentEventSave()
        val newId = generateClientUuid()
        _uiState.update {
            it.copy(
                viewMode = PlannerViewMode.CreateEvent,
                isTimelineEditMode = false,
                selectedBlockId = null,
                currentEvent = null,
                eventDraft = createDefaultDraft(newId),
                saveStatus = SaveStatus.Idle,
                validationError = null
            )
        }
    }

    fun openEventDetail(eventId: String) {
        if (_uiState.value.viewMode is PlannerViewMode.EventDetail &&
            (_uiState.value.viewMode as PlannerViewMode.EventDetail).eventId == eventId
        ) {
            return
        }

        flushCurrentEventSave()

        val existing = _uiState.value.events.firstOrNull { it.id == eventId }
        _uiState.update {
            it.copy(
                viewMode = PlannerViewMode.EventDetail(eventId),
                isTimelineEditMode = false,
                selectedBlockId = null,
                currentEvent = existing,
                eventDraft = existing?.toDraft() ?: EventFormDraft(id = eventId),
                saveStatus = SaveStatus.Idle,
                validationError = null
            )
        }

        viewModelScope.launch {
            try {
                val remoteEvent = plannerRepository.getEvent(eventId)
                _uiState.update { current ->
                    if (current.viewMode is PlannerViewMode.EventDetail &&
                        current.viewMode.eventId == eventId &&
                        current.saveStatus !is SaveStatus.Saving
                    ) {
                        current.copy(
                            currentEvent = remoteEvent,
                            eventDraft = remoteEvent.toDraft()
                        )
                    } else {
                        current
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (_: Exception) {}
        }
    }

    fun scheduleAutosave(immediate: Boolean = false) {
        if (immediate) {
            debounceSaveJob?.cancel()
            debounceSaveJob = null
            val currentDraft = _uiState.value.eventDraft
            val isCreate = (_uiState.value.viewMode is PlannerViewMode.CreateEvent)
            viewModelScope.launch {
                saveDraftInternal(currentDraft, isCreate, isExplicit = false)
            }
        } else {
            debounceSaveJob?.cancel()
            debounceSaveJob = viewModelScope.launch {
                delay(saveDebounceMillis)
                val currentDraft = _uiState.value.eventDraft
                val isCreate = (_uiState.value.viewMode is PlannerViewMode.CreateEvent)
                viewModelScope.launch {
                    saveDraftInternal(currentDraft, isCreate, isExplicit = false)
                }
            }
        }
    }

    private suspend fun saveDraftInternal(
        draftToSend: EventFormDraft,
        isCreate: Boolean,
        isExplicit: Boolean = false
    ) {
        saveMutex.withLock {
            // 1. In CreateEvent mode: if draft is untouched and this is not an explicit save request, keep local
            if (!isExplicit && isCreate && isUntouchedDraft(draftToSend)) {
                return@withLock
            }

            // 2. Validate draft:
            val validationErr = validateDraft(draftToSend)
            if (validationErr != null) {
                val isCurrent = (_uiState.value.eventDraft.id == draftToSend.id)
                if (isCurrent) {
                    _uiState.update { it.copy(validationError = validationErr) }
                }
                return@withLock
            } else {
                val isCurrent = (_uiState.value.eventDraft.id == draftToSend.id)
                if (isCurrent) {
                    _uiState.update { if (it.validationError != null) it.copy(validationError = null) else it }
                }
            }

            // 3. For existing events: check if already up-to-date and determine current expectedVersion
            val existingInList = _uiState.value.events.firstOrNull { it.id == draftToSend.id }
            val actuallyCreate = isCreate && existingInList == null
            val currentExpectedVersion = existingInList?.version ?: draftToSend.version
            if (!actuallyCreate && existingInList != null) {
                if (draftToSend.copy(version = existingInList.version) == existingInList.toDraft()) {
                    return@withLock
                }
            }

            // 4. Set saving status if current event
            val isCurrentBeforeSave = (_uiState.value.eventDraft.id == draftToSend.id)
            if (isCurrentBeforeSave) {
                _uiState.update { it.copy(saveStatus = SaveStatus.Saving) }
            }

            try {
                val savedEvent = if (actuallyCreate) {
                    val req = CreateEventRequest(
                        id = draftToSend.id,
                        name = draftToSend.name,
                        date = draftToSend.date?.trim()?.ifBlank { null },
                        startTime = draftToSend.startTime,
                        endTime = draftToSend.endTime,
                        blocks = draftToSend.blocks.map { b ->
                            CreateEventBlockRequest(
                                id = b.id,
                                blockType = b.blockType,
                                title = b.title,
                                startTime = b.startTime,
                                endTime = b.endTime,
                                instructions = b.instructions.ifBlank { null },
                                description = b.description.ifBlank { null },
                                notes = b.notes.ifBlank { null },
                                activityId = if (b.blockType == BlockType.CATALOG_GAME) b.activityId else null
                            )
                        }
                    )
                    plannerRepository.createEvent(req)
                } else {
                    val req = UpdateEventRequest(
                        expectedVersion = currentExpectedVersion,
                        name = draftToSend.name,
                        date = draftToSend.date?.trim()?.ifBlank { null },
                        startTime = draftToSend.startTime,
                        endTime = draftToSend.endTime,
                        blocks = draftToSend.blocks.map { b ->
                            UpdateEventBlockRequest(
                                id = b.id,
                                blockType = b.blockType,
                                title = b.title,
                                startTime = b.startTime,
                                endTime = b.endTime,
                                instructions = b.instructions.ifBlank { null },
                                description = b.description.ifBlank { null },
                                notes = b.notes.ifBlank { null },
                                activityId = if (b.blockType == BlockType.CATALOG_GAME) b.activityId else null
                            )
                        }
                    )
                    plannerRepository.updateEvent(draftToSend.id, req)
                }

                var scheduleFollowUpSave = false

                _uiState.update { current ->
                    val updatedEvents = (current.events.filterNot { it.id == savedEvent.id } + savedEvent)
                        .sortedWith(compareByDescending<EventResponse> { it.date ?: "" }.thenBy { it.startTime })

                    val isStillViewingThisEvent = when (current.viewMode) {
                        is PlannerViewMode.EventDetail -> current.viewMode.eventId == savedEvent.id && current.eventDraft.id == savedEvent.id
                        PlannerViewMode.CreateEvent -> current.eventDraft.id == draftToSend.id
                        PlannerViewMode.EventList -> false
                    }

                    if (!isStillViewingThisEvent) {
                        // User navigated to another event or to list!
                        // Only update events list. Keep user's active draft, viewMode, currentEvent untouched!
                        current.copy(events = updatedEvents)
                    } else {
                        // User is still viewing this event:
                        val modifiedInFlight = (current.eventDraft.copy(
                            version = draftToSend.version,
                            id = draftToSend.id
                        ) != draftToSend)

                        val nextDraft = if (modifiedInFlight) {
                            current.eventDraft.copy(
                                id = savedEvent.id,
                                version = savedEvent.version
                            )
                        } else {
                            savedEvent.toDraft()
                        }

                        if (modifiedInFlight) {
                            scheduleFollowUpSave = true
                        }

                        val nextMode = if (current.viewMode is PlannerViewMode.CreateEvent) {
                            PlannerViewMode.EventDetail(savedEvent.id)
                        } else {
                            current.viewMode
                        }

                        current.copy(
                            viewMode = nextMode,
                            currentEvent = savedEvent,
                            eventDraft = nextDraft,
                            events = updatedEvents,
                            saveStatus = SaveStatus.Saved
                        )
                    }
                }

                if (scheduleFollowUpSave) {
                    scheduleAutosave(immediate = true)
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: ConflictException) {
                _uiState.update { current ->
                    val isCurrent = (current.eventDraft.id == draftToSend.id)
                    if (isCurrent) {
                        current.copy(
                            saveStatus = SaveStatus.Conflict(
                                "Konflikt wersji: plan został zmieniony w innej sesji. Twoje zmiany w formularzu zostały zachowane. Możesz ponowić próbę lub załadować aktualną wersję z serwera."
                            )
                        )
                    } else {
                        current
                    }
                }
            } catch (e: Exception) {
                _uiState.update { current ->
                    val isCurrent = (current.eventDraft.id == draftToSend.id)
                    if (isCurrent) {
                        current.copy(
                            saveStatus = SaveStatus.Error(
                                e.message ?: "Wystąpił błąd podczas zapisywania wydarzenia"
                            )
                        )
                    } else {
                        current
                    }
                }
            }
        }
    }

    fun onNameChange(value: String) {
        _uiState.update { it.copy(eventDraft = it.eventDraft.copy(name = value), validationError = null) }
        scheduleAutosave(immediate = false)
    }

    fun onDateChange(value: String) {
        _uiState.update { it.copy(eventDraft = it.eventDraft.copy(date = value.ifBlank { null }), validationError = null) }
        scheduleAutosave(immediate = false)
    }

    fun onStartTimeChange(value: String) {
        _uiState.update { state ->
            val draft = state.eventDraft
            val isTimeFormat = value.trim().matches(Regex("""^([01]\d|2[0-3]):[0-5]\d$"""))
            if (!isTimeFormat) {
                return@update state.copy(eventDraft = draft.copy(startTime = value), validationError = null)
            }
            val newStartMin = timeToMinutes(value)
            val eventEndMin = timeToMinutes(draft.endTime)
            val minGridStart = ceilTo5Minutes(newStartMin)
            val maxGridEnd = floorTo5Minutes(eventEndMin)

            val updatedBlocks = if (newStartMin < eventEndMin) {
                if (maxGridEnd - minGridStart < 5) {
                    draft.blocks.filter { b ->
                        val bStart = timeToMinutes(b.startTime)
                        val bEnd = timeToMinutes(b.endTime)
                        bStart >= newStartMin && bEnd <= eventEndMin && (bEnd - bStart) >= 5
                    }
                } else {
                    draft.blocks.mapNotNull { b ->
                        val bStart = timeToMinutes(b.startTime)
                        val bEnd = timeToMinutes(b.endTime)
                        val duration = snapTo5Minutes(bEnd - bStart).coerceAtLeast(5)
                        if (bStart < minGridStart) {
                            val adjStart = minGridStart
                            val maxAllowedDuration = maxGridEnd - minGridStart
                            val safeDuration = duration.coerceAtMost(maxAllowedDuration).coerceAtLeast(5)
                            val adjEnd = (adjStart + safeDuration).coerceAtMost(maxGridEnd)
                            val finalStart = (adjEnd - safeDuration).coerceAtLeast(minGridStart)
                            b.copy(
                                startTime = minutesToTime(snapTo5Minutes(finalStart)),
                                endTime = minutesToTime(snapTo5Minutes(adjEnd))
                            )
                        } else {
                            b
                        }
                    }.filter { b ->
                        val bs = timeToMinutes(b.startTime)
                        val be = timeToMinutes(b.endTime)
                        bs >= newStartMin && be <= eventEndMin
                    }
                }
            } else {
                draft.blocks
            }
            state.copy(eventDraft = draft.copy(startTime = value, blocks = updatedBlocks), validationError = null)
        }
        scheduleAutosave(immediate = false)
    }

    fun onEndTimeChange(value: String) {
        _uiState.update { state ->
            val draft = state.eventDraft
            val isTimeFormat = value.trim().matches(Regex("""^([01]\d|2[0-3]):[0-5]\d$"""))
            if (!isTimeFormat) {
                return@update state.copy(eventDraft = draft.copy(endTime = value), validationError = null)
            }
            val newEndMin = timeToMinutes(value)
            val eventStartMin = timeToMinutes(draft.startTime)
            val minGridStart = ceilTo5Minutes(eventStartMin)
            val maxGridEnd = floorTo5Minutes(newEndMin)

            val updatedBlocks = if (newEndMin > eventStartMin) {
                if (maxGridEnd - minGridStart < 5) {
                    draft.blocks.filter { b ->
                        val bStart = timeToMinutes(b.startTime)
                        val bEnd = timeToMinutes(b.endTime)
                        bStart >= eventStartMin && bEnd <= newEndMin && (bEnd - bStart) >= 5
                    }
                } else {
                    draft.blocks.mapNotNull { b ->
                        val bStart = timeToMinutes(b.startTime)
                        val bEnd = timeToMinutes(b.endTime)
                        val duration = snapTo5Minutes(bEnd - bStart).coerceAtLeast(5)
                        if (bEnd > maxGridEnd) {
                            val adjEnd = maxGridEnd
                            val maxAllowedDuration = maxGridEnd - minGridStart
                            val safeDuration = duration.coerceAtMost(maxAllowedDuration).coerceAtLeast(5)
                            val adjStart = (adjEnd - safeDuration).coerceAtLeast(minGridStart)
                            val finalEnd = (adjStart + safeDuration).coerceAtMost(maxGridEnd)
                            b.copy(
                                startTime = minutesToTime(snapTo5Minutes(adjStart)),
                                endTime = minutesToTime(snapTo5Minutes(finalEnd))
                            )
                        } else {
                            b
                        }
                    }.filter { b ->
                        val bs = timeToMinutes(b.startTime)
                        val be = timeToMinutes(b.endTime)
                        bs >= eventStartMin && be <= newEndMin
                    }
                }
            } else {
                draft.blocks
            }
            state.copy(eventDraft = draft.copy(endTime = value, blocks = updatedBlocks), validationError = null)
        }
        scheduleAutosave(immediate = false)
    }

    fun toggleTimelineEditMode() {
        _uiState.update { state ->
            val next = !state.isTimelineEditMode
            state.copy(
                isTimelineEditMode = next,
                selectedBlockId = if (!next) null else state.selectedBlockId
            )
        }
    }

    fun setTimelineEditMode(enabled: Boolean) {
        _uiState.update { state ->
            state.copy(
                isTimelineEditMode = enabled,
                selectedBlockId = if (!enabled) null else state.selectedBlockId
            )
        }
    }

    fun selectBlock(blockId: String?) {
        _uiState.update { it.copy(selectedBlockId = blockId) }
    }

    fun shiftBlock(blockId: String, deltaMinutes: Int) {
        if (deltaMinutes == 0) return
        _uiState.update { state ->
            val draft = state.eventDraft
            val block = draft.blocks.firstOrNull { it.id == blockId } ?: return@update state
            val eventStart = timeToMinutes(draft.startTime)
            val eventEnd = timeToMinutes(draft.endTime)
            val minGridStart = ceilTo5Minutes(eventStart)
            val maxGridEnd = floorTo5Minutes(eventEnd)
            if (maxGridEnd - minGridStart < 5) return@update state

            val bStart = timeToMinutes(block.startTime)
            val bEnd = timeToMinutes(block.endTime)
            val rawDuration = bEnd - bStart
            val duration = snapTo5Minutes(rawDuration).coerceAtLeast(5)
            val maxAllowedDuration = maxGridEnd - minGridStart
            val safeDuration = duration.coerceAtMost(maxAllowedDuration)

            val targetStart = snapTo5Minutes(bStart + deltaMinutes)
            val maxStart = maxGridEnd - safeDuration
            val clampedStart = targetStart.coerceIn(minGridStart, maxStart)
            val clampedEnd = clampedStart + safeDuration

            val updatedBlock = block.copy(
                startTime = minutesToTime(clampedStart),
                endTime = minutesToTime(clampedEnd)
            )
            val updatedBlocks = draft.blocks.map { if (it.id == blockId) updatedBlock else it }
            state.copy(eventDraft = draft.copy(blocks = updatedBlocks))
        }
        scheduleAutosave(immediate = false)
    }

    fun resizeBlockStart(blockId: String, deltaMinutes: Int) {
        if (deltaMinutes == 0) return
        _uiState.update { state ->
            val draft = state.eventDraft
            val block = draft.blocks.firstOrNull { it.id == blockId } ?: return@update state
            val eventStart = timeToMinutes(draft.startTime)
            val eventEnd = timeToMinutes(draft.endTime)
            val minGridStart = ceilTo5Minutes(eventStart)
            val maxGridEnd = floorTo5Minutes(eventEnd)
            if (maxGridEnd - minGridStart < 5) return@update state

            val bStart = timeToMinutes(block.startTime)
            val bEnd = timeToMinutes(block.endTime)

            val effectiveEnd = floorTo5Minutes(bEnd).coerceAtMost(maxGridEnd)
            val maxStart = effectiveEnd - 5
            if (maxStart < minGridStart) return@update state

            val targetStart = snapTo5Minutes(bStart + deltaMinutes)
            val clampedStart = targetStart.coerceIn(minGridStart, maxStart)

            val updatedBlock = block.copy(
                startTime = minutesToTime(clampedStart),
                endTime = minutesToTime(effectiveEnd)
            )
            val updatedBlocks = draft.blocks.map { if (it.id == blockId) updatedBlock else it }
            state.copy(eventDraft = draft.copy(blocks = updatedBlocks))
        }
        scheduleAutosave(immediate = false)
    }

    fun resizeBlockEnd(blockId: String, deltaMinutes: Int) {
        if (deltaMinutes == 0) return
        _uiState.update { state ->
            val draft = state.eventDraft
            val block = draft.blocks.firstOrNull { it.id == blockId } ?: return@update state
            val eventStart = timeToMinutes(draft.startTime)
            val eventEnd = timeToMinutes(draft.endTime)
            val minGridStart = ceilTo5Minutes(eventStart)
            val maxGridEnd = floorTo5Minutes(eventEnd)
            if (maxGridEnd - minGridStart < 5) return@update state

            val bStart = timeToMinutes(block.startTime)
            val bEnd = timeToMinutes(block.endTime)

            val effectiveStart = ceilTo5Minutes(bStart).coerceAtLeast(minGridStart)
            val minEnd = effectiveStart + 5
            if (minEnd > maxGridEnd) return@update state

            val targetEnd = snapTo5Minutes(bEnd + deltaMinutes)
            val clampedEnd = targetEnd.coerceIn(minEnd, maxGridEnd)

            val updatedBlock = block.copy(
                startTime = minutesToTime(effectiveStart),
                endTime = minutesToTime(clampedEnd)
            )
            val updatedBlocks = draft.blocks.map { if (it.id == blockId) updatedBlock else it }
            state.copy(eventDraft = draft.copy(blocks = updatedBlocks))
        }
        scheduleAutosave(immediate = false)
    }

    fun openTimePicker(target: TimePickerTarget) {
        _uiState.update { it.copy(isTimePickerOpen = true, timePickerTarget = target) }
    }

    fun closeTimePicker() {
        _uiState.update { it.copy(isTimePickerOpen = false, timePickerTarget = null) }
    }

    fun selectTimeFromPicker(timeStr: String) {
        val target = _uiState.value.timePickerTarget
        if (target == TimePickerTarget.EVENT_START) {
            onStartTimeChange(timeStr)
        } else if (target == TimePickerTarget.EVENT_END) {
            onEndTimeChange(timeStr)
        }
        closeTimePicker()
        scheduleAutosave(immediate = true)
    }

    fun openAddBlockTypePicker(startTime: String = "", endTime: String = "") {
        val defaultStart = startTime.ifBlank { _uiState.value.eventDraft.startTime.ifBlank { "10:00" } }
        val defaultEnd = endTime.ifBlank {
            val s = timeToMinutes(defaultStart)
            val draftEndMin = timeToMinutes(_uiState.value.eventDraft.endTime.ifBlank { "16:00" })
            minutesToTime((s + 60).coerceAtMost(draftEndMin))
        }
        _uiState.update {
            it.copy(
                isAddTypePickerOpen = true,
                pendingBlockStartTime = defaultStart,
                pendingBlockEndTime = defaultEnd
            )
        }
    }

    fun closeAddBlockTypePicker() {
        _uiState.update { it.copy(isAddTypePickerOpen = false) }
    }

    fun openCustomGameEditor(startTime: String = "", endTime: String = "") {
        val s = startTime.ifBlank { _uiState.value.pendingBlockStartTime }
        val e = endTime.ifBlank { _uiState.value.pendingBlockEndTime }
        _uiState.update {
            it.copy(
                isAddTypePickerOpen = false,
                isBlockEditorOpen = true,
                editingBlock = BlockDraft(
                    id = generateClientUuid(),
                    blockType = BlockType.CUSTOM_GAME,
                    title = "",
                    startTime = s,
                    endTime = e,
                    instructions = "",
                    activityId = null
                )
            )
        }
    }

    fun addQuickOtherBlock(title: String, startTime: String = "", endTime: String = "") {
        val s = startTime.ifBlank { _uiState.value.pendingBlockStartTime }
        val e = endTime.ifBlank { _uiState.value.pendingBlockEndTime }
        val newBlock = BlockDraft(
            id = generateClientUuid(),
            blockType = BlockType.OTHER,
            title = title,
            startTime = s,
            endTime = e,
            instructions = "",
            activityId = null
        )
        _uiState.update { state ->
            state.copy(
                isAddTypePickerOpen = false,
                eventDraft = state.eventDraft.copy(blocks = state.eventDraft.blocks + newBlock)
            )
        }
        scheduleAutosave(immediate = true)
    }

    fun openCatalogPicker(startTime: String = "", endTime: String = "") {
        val s = startTime.ifBlank { _uiState.value.pendingBlockStartTime }
        val e = endTime.ifBlank { _uiState.value.pendingBlockEndTime }
        _uiState.update {
            it.copy(
                isCatalogPickerOpen = true,
                isAddTypePickerOpen = false,
                pendingBlockStartTime = s,
                pendingBlockEndTime = e
            )
        }
        loadCatalogActivities()
    }

    fun closeCatalogPicker() {
        _uiState.update { it.copy(isCatalogPickerOpen = false) }
    }

    fun loadCatalogActivities(search: String? = null) {
        viewModelScope.launch {
            _uiState.update { it.copy(isCatalogLoading = true) }
            try {
                val activities = if (activityApiClient != null) {
                    activityApiClient.listActivities(ActivityFilters(search = search)).items
                } else {
                    plannerRepository.listCatalogActivities(search)
                }
                _uiState.update { it.copy(catalogActivities = activities, isCatalogLoading = false) }
            } catch (e: CancellationException) {
                throw e
            } catch (_: Exception) {
                _uiState.update { it.copy(isCatalogLoading = false) }
            }
        }
    }

    fun selectCatalogActivity(activity: ActivityResponse) {
        val s = _uiState.value.pendingBlockStartTime
        val e = _uiState.value.pendingBlockEndTime
        val newBlock = BlockDraft(
            id = generateClientUuid(),
            blockType = BlockType.CATALOG_GAME,
            title = activity.name,
            startTime = s,
            endTime = e,
            instructions = activity.description,
            activityId = activity.id
        )
        _uiState.update { state ->
            state.copy(
                isCatalogPickerOpen = false,
                eventDraft = state.eventDraft.copy(blocks = state.eventDraft.blocks + newBlock)
            )
        }
        scheduleAutosave(immediate = true)
    }

    fun openAddBlockDialog() {
        val defaultStart = _uiState.value.eventDraft.startTime.ifBlank { "10:00" }
        val defaultEnd = _uiState.value.eventDraft.endTime.ifBlank { "11:00" }
        _uiState.update {
            it.copy(
                isBlockEditorOpen = true,
                editingBlock = BlockDraft(
                    id = generateClientUuid(),
                    blockType = BlockType.CATALOG_GAME,
                    title = "",
                    startTime = defaultStart,
                    endTime = defaultEnd,
                    instructions = "",
                    activityId = null
                )
            )
        }
    }

    fun openEditBlockDialog(block: BlockDraft) {
        _uiState.update {
            it.copy(
                isBlockEditorOpen = true,
                editingBlock = block
            )
        }
    }

    fun closeBlockDialog() {
        _uiState.update {
            it.copy(
                isBlockEditorOpen = false,
                editingBlock = null
            )
        }
    }

    fun onEditingBlockChange(updated: BlockDraft) {
        _uiState.update { it.copy(editingBlock = updated) }
    }

    fun onSaveBlock(block: BlockDraft) {
        if (block.title.isBlank()) {
            return
        }

        _uiState.update { state ->
            val eventStart = timeToMinutes(state.eventDraft.startTime)
            val eventEnd = timeToMinutes(state.eventDraft.endTime)
            val bStart = timeToMinutes(block.startTime)
            val bEnd = timeToMinutes(block.endTime)
            if (bStart < eventStart || bEnd > eventEnd || bEnd - bStart < 5) {
                // Reject block outside event bounds or shorter than 5 minutes
                return@update state.copy(
                    isBlockEditorOpen = false,
                    editingBlock = null
                )
            }

            val existingIndex = state.eventDraft.blocks.indexOfFirst { it.id == block.id }
            val updatedBlocks = if (existingIndex >= 0) {
                state.eventDraft.blocks.toMutableList().apply { set(existingIndex, block) }
            } else {
                state.eventDraft.blocks + block
            }
            state.copy(
                eventDraft = state.eventDraft.copy(blocks = updatedBlocks),
                isBlockEditorOpen = false,
                editingBlock = null
            )
        }
        scheduleAutosave(immediate = true)
    }

    fun onDeleteBlock(blockId: String) {
        _uiState.update { state ->
            state.copy(
                eventDraft = state.eventDraft.copy(
                    blocks = state.eventDraft.blocks.filterNot { it.id == blockId }
                ),
                selectedBlockId = if (state.selectedBlockId == blockId) null else state.selectedBlockId
            )
        }
        scheduleAutosave(immediate = true)
    }

    fun openFavoritesPicker() {
        _uiState.update { it.copy(isFavoritesPickerOpen = true) }
        loadFavorites()
    }

    fun closeFavoritesPicker() {
        _uiState.update { it.copy(isFavoritesPickerOpen = false) }
    }

    fun selectFavoriteActivity(activity: ActivityResponse) {
        val currentBlock = _uiState.value.editingBlock
        val updated = (currentBlock ?: BlockDraft(
            id = generateClientUuid(),
            startTime = _uiState.value.eventDraft.startTime.ifBlank { "10:00" },
            endTime = _uiState.value.eventDraft.endTime.ifBlank { "11:00" }
        )).copy(
            blockType = BlockType.CATALOG_GAME,
            title = activity.name,
            instructions = activity.description,
            activityId = activity.id
        )

        _uiState.update {
            it.copy(
                editingBlock = updated,
                isBlockEditorOpen = true,
                isFavoritesPickerOpen = false
            )
        }
    }

    fun toggleFavorite(activity: ActivityResponse) {
        val isFav = _uiState.value.favoriteIds.contains(activity.id)
        viewModelScope.launch {
            try {
                if (isFav) {
                    plannerRepository.removeFavorite(activity.id)
                    _uiState.update { state ->
                        state.copy(
                            favoriteIds = state.favoriteIds - activity.id,
                            favorites = state.favorites.filterNot { it.id == activity.id }
                        )
                    }
                } else {
                    plannerRepository.addFavorite(activity.id)
                    _uiState.update { state ->
                        state.copy(
                            favoriteIds = state.favoriteIds + activity.id,
                            favorites = state.favorites + activity
                        )
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (_: Exception) {}
        }
    }

    fun saveEvent() {
        if (_uiState.value.saveStatus is SaveStatus.Conflict) {
            retrySave()
            return
        }
        debounceSaveJob?.cancel()
        debounceSaveJob = null
        val currentDraft = _uiState.value.eventDraft
        val isCreate = (_uiState.value.viewMode is PlannerViewMode.CreateEvent)
        viewModelScope.launch {
            saveDraftInternal(currentDraft, isCreate, isExplicit = true)
        }
    }

    fun retrySave() {
        debounceSaveJob?.cancel()
        debounceSaveJob = null

        val draftToRetry = _uiState.value.eventDraft
        val mode = _uiState.value.viewMode
        val isCreate = (mode is PlannerViewMode.CreateEvent)
        val eventId = draftToRetry.id

        if (isCreate || eventId.isBlank()) {
            viewModelScope.launch {
                saveDraftInternal(draftToRetry, isCreate = true, isExplicit = true)
            }
            return
        }

        viewModelScope.launch {
            val freshEvent = try {
                plannerRepository.getEvent(eventId)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                _uiState.update { current ->
                    val isStillActive = (current.eventDraft.id == eventId)
                    if (isStillActive) {
                        current.copy(
                            saveStatus = SaveStatus.Error(
                                "Nie udało się pobrać aktualnej wersji z serwera: ${e.message ?: "Błąd sieci"}. Spróbuj ponownie."
                            )
                        )
                    } else {
                        current
                    }
                }
                return@launch
            }

            _uiState.update { current ->
                val updatedEvents = (current.events.filterNot { it.id == freshEvent.id } + freshEvent)
                    .sortedWith(compareByDescending<EventResponse> { it.date ?: "" }.thenBy { it.startTime })

                val isStillActive = when (current.viewMode) {
                    is PlannerViewMode.EventDetail -> current.viewMode.eventId == eventId && current.eventDraft.id == eventId
                    PlannerViewMode.CreateEvent -> false
                    PlannerViewMode.EventList -> false
                }

                if (isStillActive) {
                    current.copy(
                        events = updatedEvents,
                        currentEvent = freshEvent,
                        eventDraft = current.eventDraft.copy(version = freshEvent.version)
                    )
                } else {
                    current.copy(events = updatedEvents)
                }
            }

            val draftWithFreshVersion = draftToRetry.copy(version = freshEvent.version)
            saveDraftInternal(draftWithFreshVersion, isCreate = false, isExplicit = true)
        }
    }

    fun reloadCurrentEventFromServer() {
        val currentEventId = _uiState.value.eventDraft.id
        if (currentEventId.isBlank()) return

        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            try {
                val remoteEvent = plannerRepository.getEvent(currentEventId)
                _uiState.update { state ->
                    state.copy(
                        isLoading = false,
                        currentEvent = remoteEvent,
                        eventDraft = remoteEvent.toDraft(),
                        saveStatus = SaveStatus.Idle,
                        validationError = null
                    )
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        generalError = "Nie udało się przeładować z serwera: ${e.message}"
                    )
                }
            }
        }
    }

    fun deleteCurrentEvent() {
        debounceSaveJob?.cancel()
        debounceSaveJob = null
        val currentEventId = _uiState.value.eventDraft.id
        if (currentEventId.isBlank()) return

        viewModelScope.launch {
            saveMutex.withLock {
                val existing = _uiState.value.events.firstOrNull { it.id == currentEventId }
                val expectedVersion = existing?.version ?: _uiState.value.eventDraft.version

                _uiState.update { it.copy(isLoading = true, generalError = null) }
                try {
                    plannerRepository.deleteEvent(currentEventId, expectedVersion)
                    _uiState.update { state ->
                        state.copy(
                            isLoading = false,
                            viewMode = PlannerViewMode.EventList,
                            currentEvent = null,
                            events = state.events.filterNot { it.id == currentEventId },
                            saveStatus = SaveStatus.Idle
                        )
                    }
                } catch (e: CancellationException) {
                    throw e
                } catch (e: ConflictException) {
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            saveStatus = SaveStatus.Conflict(
                                "Konflikt wersji: wydarzenie zostało zmodyfikowane w innej sesji i nie może zostać usunięte na podstawie nieaktualnej wersji. Twoje zmiany w formularzu zostały zachowane. Możesz przeładować aktualną wersję z serwera."
                            )
                        )
                    }
                } catch (e: Exception) {
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            generalError = "Błąd podczas usuwania: ${e.message}"
                        )
                    }
                }
            }
        }
    }

    fun validateDraft(draft: EventFormDraft): String? {
        if (draft.name.trim().isEmpty()) {
            return "Nazwa wydarzenia jest wymagana"
        }
        if (draft.name.length > 200) {
            return "Nazwa wydarzenia nie może przekraczać 200 znaków"
        }
        if (!draft.date.isNullOrBlank() && !isValidIsoDate(draft.date)) {
            return "Data musi być prawidłową datą kalendarzową w formacie YYYY-MM-DD"
        }
        if (!draft.startTime.trim().matches(Regex("""^([01]\d|2[0-3]):[0-5]\d$"""))) {
            return "Godzina rozpoczęcia musi mieć format HH:mm"
        }
        if (!draft.endTime.trim().matches(Regex("""^([01]\d|2[0-3]):[0-5]\d$"""))) {
            return "Godzina zakończenia musi mieć format HH:mm"
        }
        for (b in draft.blocks) {
            if (b.title.trim().isEmpty()) {
                return "Tytuł każdego bloku programu jest wymagany"
            }
            if (b.title.length > 200) {
                return "Tytuł bloku programu nie może przekraczać 200 znaków"
            }
            if (b.blockType == BlockType.CATALOG_GAME && b.activityId.isNullOrBlank()) {
                return "Dla zabawy z katalogu należy wybrać zabawę źródłową"
            }
        }
        return null
    }
}
